import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	OnDestroy,
	ViewEncapsulation
} from '@angular/core';
import { AgFilterComponent } from '@ag-grid-community/angular';
import { IDoesFilterPassParams, IFilterParams, IRowNode, ColDef } from '@ag-grid-community/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';

import { DimensionFilter, FilterType, ShipmentDimensionFilter, ProducedDimensionFilter } from '../../models/ag-grid';
import { DatacenterType } from '../../models/datacenter-aggrid-view';
import { FilterInstance } from '../../services/datacenter-filter';

interface FieldMap {
	thickness: string;
	width: string;
	length: string;
}

interface DimensionFilterParams {
	fieldMap?: FieldMap;
	filterType?: string;
}

type ColDefForFilter = Omit<ColDef, 'filterParams'> & {
	filterParams?: DimensionFilterParams;
	fields?: string[];
};

type FilterParamsWithFieldMap = Omit<IFilterParams<Record<string, unknown>>, 'colDef'> & {
	colDef: ColDefForFilter;
};

@Component({
	selector: 'copo-datacenter-dimension-filter',
	templateUrl: './datacenter-dimension-filter.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule
	]
})
export class DatacenterDimensionFilterComponent implements AgFilterComponent, FilterInstance, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	thicknessText = '';
	widthText = '';
	lengthText = '';
	datacenterTypeSub?: Subscription;
	datacenterType: DatacenterType = 'order';
	thicknessTextControl = new FormControl(
		this.thicknessText,
		{ nonNullable: true, validators: Validators.pattern('[.0-9]*') }
	);

	widthTextControl = new FormControl(
		this.widthText,
		{ nonNullable: true, validators: Validators.pattern('[.0-9]*') }
	);

	lengthTextControl = new FormControl(
		this.lengthText,
		{ nonNullable: true, validators: Validators.pattern('[.0-9]*') }
	);

	params?: IFilterParams<Record<string, unknown>>;
	private readonly thicknessSubscription: Subscription;
	private readonly widthSubscription: Subscription;
	private readonly lengthSubscription: Subscription;
	private isClientSide = false;
	private fieldMap: FieldMap = { thickness: 'thickness', width: 'width', length: 'length' };
	constructor(
		private readonly route: ActivatedRoute,
	) {
		this.datacenterTypeSub = this.route.data.subscribe(res => {
			this.datacenterType = <DatacenterType> res['type'];
		});
		this.thicknessSubscription = this.thicknessTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.thicknessText = this.isValidValue(value) ? value : '';
			if (this.params) {
				this.params.filterChangedCallback();
			}
		});
		this.widthSubscription = this.widthTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.widthText = this.isValidValue(value) ? value : '';
			if (this.params) {
				this.params.filterChangedCallback();
			}
		});
		this.lengthSubscription = this.lengthTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.lengthText = this.isValidValue(value) ? value : '';
			if (this.params) {
				this.params.filterChangedCallback();
			}
		});
	}

	agInit(params: IFilterParams<Record<string, unknown>>) {
		this.params = params;
		const rowModelType = params.rowModel.getType();
		this.isClientSide = rowModelType === 'clientSide';
		const p = <FilterParamsWithFieldMap>params;
		const map: FieldMap | undefined = p.colDef.filterParams?.fieldMap;
		if (map) {
			this.fieldMap = map;
		} else {
			const { fields } = p.colDef;
			if (Array.isArray(fields) && fields.length === 3) {
				const [thickness, width, length] = fields;
				this.fieldMap = { thickness, width, length };
			}
		}
	}

	isFilterActive() {
		return this.isValidFilter(this.thicknessText)
			|| this.isValidFilter(this.widthText)
			|| this.isValidFilter(this.lengthText);
	}

	doesFilterPass(_params: IDoesFilterPassParams<Record<string, unknown>>) {
		if (!this.isClientSide) {
			return true;
		}
		const tVal = this.getColumnValue(_params.node, this.fieldMap.thickness);
		const wVal = this.getColumnValue(_params.node, this.fieldMap.width);
		const lVal = this.getColumnValue(_params.node, this.fieldMap.length);

		return this.checkValue(this.thicknessText, Number(tVal ?? '').toString())
			&& this.checkValue(this.widthText, Number(wVal ?? '').toString())
			&& this.checkValue(this.lengthText, Number(lVal ?? '').toString());
	}

	getModel(): DimensionFilter | ShipmentDimensionFilter | ProducedDimensionFilter | undefined {
		if (this.isFilterActive()) {
			if (this.datacenterType === 'shipment') {
				return {
					customFilterName: FilterType.SHIPMENT_DIMENSION_FILTER,
					thicknessDimensionConcat: this.thicknessText,
					widthDimensionConcat: this.widthText,
					lengthDimensionConcat: this.lengthText
				};
			}
			const { thickness, width, length } = this.fieldMap;
			const isProduced =
				thickness.startsWith('produced')
				|| width.startsWith('produced')
				|| length.startsWith('produced');

			if (isProduced) {
				return {
					customFilterName: FilterType.PRODUCED_DIMENSION_FILTER,
					producedThickness: this.thicknessText,
					producedWidth: this.widthText,
					producedLength: this.lengthText
						? String(Number(this.lengthText) * 1000)
						: ''
				};
			}
			return {
				customFilterName: FilterType.DIMENSION_FILTER,
				thickness: this.thicknessText,
				width: this.widthText,
				length: this.lengthText
			};
		}
		return undefined;
	}

	setModel(dimensionFilter: DimensionFilter | ProducedDimensionFilter | null) {
		if (!dimensionFilter) {
			this.thicknessText = '';
			this.widthText = '';
			this.lengthText = '';
			this.thicknessTextControl.reset();
			this.widthTextControl.reset();
			this.lengthTextControl.reset();
			this.params?.filterChangedCallback();
			return;
		}

		if (dimensionFilter.customFilterName === FilterType.PRODUCED_DIMENSION_FILTER) {
			this.thicknessText = dimensionFilter.producedThickness;
			this.widthText = dimensionFilter.producedWidth;
			this.lengthText = dimensionFilter.producedLength;
		} else {
			this.thicknessText = dimensionFilter.thickness;
			this.widthText = dimensionFilter.width;
			this.lengthText = dimensionFilter.length;
		}

		this.thicknessTextControl.setValue(this.thicknessText, { emitEvent: false });
		this.widthTextControl.setValue(this.widthText, { emitEvent: false });
		this.lengthTextControl.setValue(this.lengthText, { emitEvent: false });
		this.params?.filterChangedCallback();
	}

	getCustomFilterName(): FilterType.DIMENSION_FILTER | FilterType.SHIPMENT_DIMENSION_FILTER {
		if ((<FilterParamsWithFieldMap | undefined> this.params)?.colDef.filterParams?.filterType
			=== 'SHIPMENT_DIMENSION_FILTER') {
			return FilterType.SHIPMENT_DIMENSION_FILTER;
		}
		return this.datacenterType === 'shipment' ? FilterType.SHIPMENT_DIMENSION_FILTER : FilterType.DIMENSION_FILTER;
	}

	ngOnDestroy() {
		this.thicknessSubscription.unsubscribe();
		this.widthSubscription.unsubscribe();
		this.lengthSubscription.unsubscribe();
	}

	resetThickness() {
		this.thicknessText = '';
		this.thicknessTextControl.reset();
	}

	resetWidth() {
		this.widthText = '';
		this.widthTextControl.reset();
	}

	resetLength() {
		this.lengthText = '';
		this.lengthTextControl.reset();
		if (this.params) {
			this.params.filterChangedCallback();
		}
	}

	private isValidFilter(value: string) {
		return value !== '' && !Number.isNaN(Number(value));
	}

	private isValidValue(value: string) {
		const pattern = /^[\d.]*$/;
		return pattern.test(value);
	}

	private getColumnValue(node: IRowNode<Record<string, unknown>>, index: string) {
		if (!this.params || typeof node.data !== 'object') {
			return undefined;
		}
		const { data } = node;
		if (data[index]) {
			return data[index];
		}
		return undefined;
	}

	private checkValue(filterVal: string, originalVal: string) {
		if (originalVal && filterVal.length > 0) {
			return filterVal === originalVal;
		}
		return true;
	}
}
